// Analytics.tsx — экран «Аналитика» (Ф5). Completion-rate за период: KPI из
// totals, тренд сдачи по дням и разрез по сотрудникам (из /api/analytics).
// Графики — чистый CSS (.chart/.bar из темы), без граф-библиотеки (правило 9).
//
// Метрики survey-agnostic (статусы run'ов), как и API: контент-метрики мокапа
// (опоздания/недовольные) зависят от кодов вопросов конкретной анкеты — их в
// /api/analytics нет, поэтому здесь честно показываем сдачу отчётов, не контент.

import { useState } from 'react';
import { api } from '../api/client';
import type { AnalyticsResponse } from '../api/contract';
import { StateCard } from '../components/StateBlock';
import { useApi } from '../lib/useApi';

const PERIODS = [
  { key: 'week', label: 'Неделя', days: 7 },
  { key: 'month', label: 'Месяц', days: 30 },
  { key: 'quarter', label: 'Квартал', days: 90 },
] as const;
type PeriodKey = (typeof PERIODS)[number]['key'];

/** YYYY-MM-DD из локальной даты браузера. Для окна-фильтра локального дня
 *  достаточно (точную TZ tenant'а ради границы диапазона не тянем). */
function ymd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** {from, to} за последние `days` дней, включая сегодня. */
function rangeFor(days: number): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now);
  from.setDate(from.getDate() - (days - 1));
  return { from: ymd(from), to: ymd(now) };
}

/** rate 0..1 → класс полосы по «светофору» сдачи. */
function barClass(rate: number): string {
  if (rate >= 0.8) return 'bar';
  if (rate >= 0.5) return 'bar warn';
  return 'bar bad';
}

/** rate 0..1 → CSS-переменная цвета (для чисел/процентов). */
function rateColor(rate: number): string {
  if (rate >= 0.8) return 'var(--green)';
  if (rate >= 0.5) return 'var(--yellow)';
  return 'var(--red)';
}

const pct = (rate: number) => `${Math.round(rate * 100)}%`;

/** День месяца из YMD без TZ-сдвига (для подписи столбца). */
function dayNum(ymdStr: string): string {
  return String(Number(ymdStr.split('-')[2] ?? 0));
}

export default function Analytics() {
  const [period, setPeriod] = useState<PeriodKey>('month');
  const { days } = PERIODS.find((p) => p.key === period) ?? PERIODS[1];
  const { from, to } = rangeFor(days);
  const { data, loading, error } = useApi<AnalyticsResponse>(
    () => api.analytics(from, to),
    [period],
  );

  const t = data?.totals;
  const empty = !!t && t.scheduled === 0;

  // Тренд по дням: высота столбца = completion rate дня. Сортировка по дате
  // приходит из API. Для длинных периодов столбцы тонкие — оборачиваем в скролл.
  const byDay = data?.byDay ?? [];
  const manyDays = byDay.length > 14;

  // По сотрудникам — худшие сверху (actionable: кто пропускает).
  const byResp = [...(data?.byRespondent ?? [])].sort(
    (a, b) => a.completionRate - b.completionRate || a.completed - b.completed,
  );

  return (
    <section>
      <div className="ph">
        <h1>Аналитика</h1>
        <div className="pill-period">
          {PERIODS.map((p) => (
            <button
              type="button"
              key={p.key}
              className={p.key === period ? 'on' : undefined}
              onClick={() => setPeriod(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <p className="sub">Сдача ежедневных отчётов за период — тренд и разрез по людям.</p>

      {loading && <StateCard kind="loading" message="Загрузка…" />}
      {error && <StateCard kind="error" message={error} />}
      {empty && <StateCard kind="empty" message="За период нет назначенных отчётов." />}

      {data && t && !empty && (
        <>
          <div className="kpis">
            <div className="kpi">
              <div className="l">Сдаваемость</div>
              <div className="v" style={{ color: rateColor(t.completionRate) }}>
                {pct(t.completionRate)}
              </div>
              <div className="d">
                {t.completed} из {t.scheduled} отчётов
              </div>
            </div>
            <div className="kpi">
              <div className="l">Отчётов ожидалось</div>
              <div className="v">{t.scheduled}</div>
              <div className="d">за {days} дн.</div>
            </div>
            <div className="kpi">
              <div className="l">Не сдано</div>
              <div className="v" style={{ color: t.abandoned ? 'var(--red)' : undefined }}>
                {t.abandoned}
              </div>
              <div className="d">брошено / не завершено</div>
            </div>
            <div className="kpi">
              <div className="l">Не начато</div>
              <div className="v">{t.pending + t.inProgress}</div>
              <div className="d">
                {t.inProgress} заполняли · {t.pending} ждут
              </div>
            </div>
          </div>

          <div className="card">
            <h3>
              Сдаваемость по дням <span className="tag">высота = % сдачи за день</span>
            </h3>
            <div className="chart" style={manyDays ? { overflowX: 'auto' } : undefined}>
              {byDay.map((d) => {
                const r = d.scheduled === 0 ? 0 : d.completed / d.scheduled;
                const h = d.scheduled === 0 ? 0 : Math.max(r * 100, 3);
                return (
                  <div
                    className="bar-wrap"
                    key={d.date}
                    style={manyDays ? { minWidth: 26 } : undefined}
                  >
                    <div
                      className={barClass(r)}
                      style={{ height: `${h}%` }}
                      title={`${d.date} · ${d.completed}/${d.scheduled} · ${pct(r)}`}
                    >
                      <span className="val">{d.completed}</span>
                    </div>
                    <span className="blab">{dayNum(d.date)}</span>
                  </div>
                );
              })}
            </div>
            <div className="legend">
              <span style={{ color: 'var(--muted)' }}>
                Каждый столбец — день периода. Цвет: зелёный ≥80%, жёлтый 50–80%, красный &lt;50%.
                Число — сколько отчётов сдано в этот день.
              </span>
            </div>
          </div>

          <div className="card" style={{ marginTop: 18 }}>
            <h3>
              По сотрудникам <span className="tag">кто пропускает</span>
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginTop: 4 }}>
              {byResp.map((r) => (
                <div key={r.respondentId}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>
                      {r.name}{' '}
                      <small style={{ color: 'var(--muted)' }}>{r.role}</small>
                    </span>
                    <b style={{ color: rateColor(r.completionRate) }}>
                      {r.completed}/{r.scheduled} · {pct(r.completionRate)}
                    </b>
                  </div>
                  <div
                    style={{
                      height: 8,
                      background: 'var(--panel2)',
                      borderRadius: 5,
                      marginTop: 4,
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${Math.round(r.completionRate * 100)}%`,
                        background: rateColor(r.completionRate),
                        borderRadius: 5,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
